import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';

/**
 * Channel visibility utilities
 * Handles hover detection, auto-hide timers, and device-specific visibility logic
 */
export const createChannelVisibilityService = () => {
  /**
   * Check if device has hover capability and handle device-specific overrides
   * webOS always shows the list, others check hover capability
   * @returns {boolean} Whether to show the list
   */
  const isInRoomTvDevice = (deviceList) =>
    deviceList.isWEBOS ||
    deviceList.isSAMSUNG ||
    deviceList.isTIZEN ||
    deviceList.isTIZEN_TEP ||
    deviceList.isANDROID ||
    deviceList.isLG;

  const checkPointer = () => {
    const deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);

    // In-room TVs and browser dev (DEFAULT) use remote/mouse — always show channel list.
    // Hover media queries report "none" on most hotel TVs and many touch laptops, which
    // previously hid the baseline carousel entirely (showList gate in channelsList.js).
    if (isInRoomTvDevice(deviceList) || deviceList.isDEFAULT) {
      return true;
    }

    return !matchMedia('(hover: none)').matches;
  };

  /**
   * Determine if list should be visible (animated in/out)
   * Used for auto-hide after timeout in fullscreen
   * @param {boolean} displayFullScreen - Is player in fullscreen
   * @param {number} timeout - Current timeout value
   * @returns {Object} { shouldShow: boolean, newTimeout: number }
   */
  const getSlideListState = (displayFullScreen, currentTimeout, isComponentMounted) => {
    if (displayFullScreen) {
      // Show list initially
      return {
        shouldShow: true,
        newTimeout: setTimeout(() => {
          if (isComponentMounted) {
            // Return a flag - component will handle setState
            return { shouldHide: true };
          }
        }, 20000), // 20 second auto-hide
      };
    } else {
      // Always show list when not fullscreen
      if (currentTimeout) clearTimeout(currentTimeout);
      return {
        shouldShow: true,
        newTimeout: null,
      };
    }
  };

  /**
   * Subscribe to mouse movement and reset auto-hide timer
   * @param {Function} onMouseMove - Callback when mouse moves
   */
  const setupMouseListener = (onMouseMove) => {
    const unsubscribe = () => {
      // Return unsubscribe function for cleanup
    };
    return unsubscribe;
  };

  /**
   * Force visibility state based on device type and conditions
   * @param {Object} state - Current state
   * @returns {boolean} Should show list
   */
  const shouldForceShowList = (state) => {
    if (checkPointer()) {
      return true;
    }

    return state.showList;
  };

  return {
    checkPointer,
    getSlideListState,
    setupMouseListener,
    shouldForceShowList,
  };
};
